export const RECENTLY_VIEWED_STORAGE_KEY = 'ff_recently_viewed_v1';
export const MAX_RECENTLY_VIEWED = 50;
// 오래된 스냅샷은 정보가 낡으므로 약 3개월이 지나면 버린다
export const RECENTLY_VIEWED_TTL_MS = 90 * 24 * 60 * 60 * 1000;

// RecentlyViewedCard가 그리는 필드와, 나중에 쓸 수 있게 미리 저장하는 필드
const SNAPSHOT_FIELDS = [
    'title',
    'brand',
    'status',
    'price',
    'discount_rate',
    // 저장만 하고 아직 화면에는 쓰지 않음
    'original_price',
    'size',
    'condition',
];

function getDefaultStorage() {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
        // 사이트 데이터가 차단되면 접근만 해도 오류가 난다
        return null;
    }
}

/**
 * 필요한 필드만 남겨 50개를 저장해도 용량이 작게 유지되도록 한다.
 */
export function toSnapshot(product) {
    const snapshot = { id: String(product.id) };
    SNAPSHOT_FIELDS.forEach((field) => {
        if (product[field] !== undefined) {
            snapshot[field] = product[field];
        }
    });
    snapshot.resizedSmallImages = (product.resizedSmallImages ?? []).slice(0, 1);
    return snapshot;
}

// 저장값은 사용자가 고치거나 예전 버전이 썼을 수 있으므로 카드가 읽는 값을 확인한다
function isValidSnapshot(product, id) {
    return Boolean(product)
        && product.id === id
        && typeof product.title === 'string'
        && typeof product.price === 'number'
        && Array.isArray(product.resizedSmallImages)
        && typeof product.resizedSmallImages[0] === 'string'
        // 선택 필드라도 객체가 들어 있으면 렌더링 중 오류로 페이지 전체가 깨진다
        && isOptional(product.brand, 'string')
        && isOptional(product.status, 'string')
        && isOptional(product.discount_rate, 'number');
}

function isOptional(value, type) {
    return value === undefined || value === null || typeof value === type;
}

function isValidEntry(entry, now) {
    return Boolean(entry)
        && typeof entry.id === 'string'
        && typeof entry.viewedAt === 'number'
        && isValidSnapshot(entry.product, entry.id)
        && now - entry.viewedAt < RECENTLY_VIEWED_TTL_MS;
}

/**
 * @returns {{id: string, viewedAt: number, product: object}[]} 최근 순 기록
 */
export function readEntries(storage = getDefaultStorage(), now = Date.now()) {
    try {
        const parsed = JSON.parse(storage?.getItem(RECENTLY_VIEWED_STORAGE_KEY) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter((entry) => isValidEntry(entry, now)) : [];
    } catch {
        return [];
    }
}

function writeEntries(entries, storage) {
    try {
        storage?.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // 용량 초과나 저장소 차단 시에도 메모리의 결과는 그대로 쓴다
    }
    return entries;
}

export function addEntry(product, { storage = getDefaultStorage(), now = Date.now() } = {}) {
    const snapshot = toSnapshot(product);
    const stored = readEntries(storage, now);
    if (!isValidSnapshot(snapshot, snapshot.id)) {
        // 제목, 가격, 이미지가 없으면 카드를 그릴 수 없다
        return stored;
    }
    const others = stored.filter((entry) => entry.id !== snapshot.id);
    const entries = [{ id: snapshot.id, viewedAt: now, product: snapshot }, ...others];
    return writeEntries(entries.slice(0, MAX_RECENTLY_VIEWED), storage);
}

export function removeEntry(id, { storage = getDefaultStorage(), now = Date.now() } = {}) {
    const stored = readEntries(storage, now);
    const entries = stored.filter((entry) => entry.id !== String(id));
    return entries.length === stored.length ? stored : writeEntries(entries, storage);
}
